import { useState } from 'react';
import { Alert, Badge, Button, ConfirmDialog, Table } from '@dreamtree-org/twreact-ui';
import { Copy } from 'lucide-react';
import SectionCard from './SectionCard';
import { formatDate, errorText } from '@/lib/format';

/**
 * Connected AI assistants (ADR 0003, D48), shared by the store admin, the shopper
 * account and the platform console. Shows the URL to add to an assistant and the
 * live connections, each with Disconnect. Which rows a viewer gets, and whether they
 * may end them, is decided on the server; this only lays them out.
 *
 *   connectUrls  [{ label, url }] — what to paste into the assistant
 *   rows         connections from the API ({ kind, id, client, surface, access, person, connected_at, last_used_at })
 *   showPerson   add the "Who" column (a manager's view of everyone's)
 *   onDisconnect (row) => Promise — ends one; errors are shown here
 */
// Access label and tone come with each row from the server (lib/oauth/access, the one source).

const personOf = (p) => (p ? p.name ? `${p.name} (${p.email || p.phone || p.number})` : p.email || p.phone || p.number : '—');

export default function AssistantConnections({ title = 'Connected AI assistants', connectUrls = [], rows = [], showPerson = false, onDisconnect, emptyText = 'No assistant is connected.' }) {
  const [ending, setEnding] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
    } catch {
      setCopied(null);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onDisconnect(ending);
      setEnding(null);
    } catch (ex) {
      setError(errorText(ex));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: 'client', label: 'Assistant', render: (r) => <span className="font-medium">{r.client}</span> },
    ...(showPerson ? [{ key: 'person', label: 'Who', render: (r) => personOf(r.person) }] : []),
    { key: 'surface', label: 'Connected to', hideOnMobile: true, render: (r) => (r.surface && r.surface.name) || '—' },
    { key: 'access', label: 'Access', render: (r) => <Badge size="sm" variant={r.access_tone || 'default'}>{r.access_label || r.access}</Badge> },
    { key: 'connected_at', label: 'Connected', hideOnMobile: true, render: (r) => formatDate(r.connected_at) },
    { key: 'last_used_at', label: 'Last used', render: (r) => (r.last_used_at ? formatDate(r.last_used_at) : 'Not yet') },
    { key: 'end', label: '', render: (r) => <Button size="xs" variant="ghost" onClick={() => setEnding(r)}>Disconnect</Button> },
  ];

  return (
    <SectionCard title={title}>
      {connectUrls.length ? (
        <div className="space-y-2 text-sm">
          <p className="opacity-80">To connect Claude, ChatGPT or another assistant, add this address as a custom connector, then sign in when it asks.</p>
          {connectUrls.map((c) => (
            <div key={c.url} className="flex flex-wrap items-center gap-2 rounded-lg border border-[var(--app-border)] p-2">
              {connectUrls.length > 1 ? <span className="font-medium">{c.label}</span> : null}
              <code className="min-w-0 flex-1 break-all text-xs">{c.url}</code>
              <Button size="xs" variant="outline" leftIcon={<Copy className="h-3 w-3" />} onClick={() => copy(c.url)}>{copied === c.url ? 'Copied' : 'Copy'}</Button>
            </div>
          ))}
        </div>
      ) : null}
      {error ? <Alert variant="error">{error}</Alert> : null}
      {rows.length ? (
        <Table data={rows} columns={columns} showSerial={false} withAction={false} showToolbar={false} sortable={false} />
      ) : (
        <p className="text-sm opacity-70">{emptyText}</p>
      )}
      {ending ? (
        <ConfirmDialog
          isOpen
          title={`Disconnect ${ending.client}${showPerson && ending.person ? ` for ${personOf(ending.person)}` : ''}?`}
          description="It stops working at once and cannot reconnect without signing in again."
          confirmLabel="Disconnect"
          destructive
          loading={busy}
          onCancel={() => setEnding(null)}
          onConfirm={confirm}
        />
      ) : null}
    </SectionCard>
  );
}
